import * as React from "react";
import { Check, Loader2 } from "lucide-react";
import usePlacesAutocomplete, { getGeocode, getLatLng } from "use-places-autocomplete";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "./command";
import { cn } from "../../lib/utils";

interface PlacesAutocompleteProps {
  placeholder: string
  value: string
  onChange: (value: string, details?: { address: string; lat: number; lng: number }) => void
  className?: string
  zoneLabel?: string
}

export function PlacesAutocomplete({ placeholder, value, onChange, className, zoneLabel }: PlacesAutocompleteProps) {
  const [displayValue, setDisplayValue] = React.useState(value)

  const {
    ready,
    value: inputValue,
    suggestions: { status, data },
    setValue,
    clearSuggestions,
  } = usePlacesAutocomplete({
    callbackName: "initMap",
    requestOptions: { componentRestrictions: { country: "NG" } },
    debounce: 300,
  })

  React.useEffect(() => {
    if (value) {
      setDisplayValue(value)
    }
  }, [value])

  const handleSelect = async (address: string) => {
    setValue(address, false)
    clearSuggestions()
    setDisplayValue(address)

    try {
      const results = await getGeocode({ address })
      const { lat, lng } = await getLatLng(results[0])
      onChange(address, { address, lat, lng })
    } catch (error) {
      console.error("Error: ", error)
      onChange(address)
    }
  }

  const handleInputChange = (value: string) => {
    setValue(value)
    setDisplayValue(value)
  }

  return (
    <div className="relative">
      {zoneLabel && (
        <div className="">
          <div className="bg-blue-100 p-2 rounded-none border-l-4 border-blue-600 text-xs text-gray-500 ">{zoneLabel}</div>
        </div>
      )}
      <Command>
        <CommandInput
          placeholder={`${placeholder.toLowerCase()}...`}
          value={inputValue}
          onValueChange={handleInputChange}
          disabled={!ready}
        />
        <CommandList className={cn('z-10 max-h-60 overflow-y-auto bg-white shadow-lg rounded-md border border-gray-200',
          status === "OK" ? "z-20" : ""
        )}>
          {!ready && (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
            </div>
          )}

          {ready && status === "OK" && (
            <CommandGroup>
              {data.map(({ place_id, description }) => (
                <CommandItem key={place_id} value={description} onSelect={() => handleSelect(description)} className="flex items-center">
                  <Check
                    className={cn("mr-2 h-4 w-4", displayValue === description ? "opacity-100" : "opacity-0")}
                  />
                  <span>{description}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          )}

          {ready && status === "ZERO_RESULTS" && <CommandEmpty>No locations found.</CommandEmpty>}

          {ready && status === "NOT_FOUND" && <CommandEmpty>Location not found.</CommandEmpty>}

          {ready && status === "OVER_QUERY_LIMIT" && (
            <CommandEmpty>Query limit exceeded. Please try again later.</CommandEmpty>
          )}
        </CommandList>
      </Command>
    </div>
  )
}
